import {useEffect, useRef} from 'react'
import {useLocation, useNavigate} from 'react-router'

import {HIERARCHY_PICKER_CHANGE_STATE} from '../utils'

import {useSuiteHierarchyId} from './useSuiteHierarchyId'

/**
 * True when the user switched to another node in the Suite hierarchy picker, which marks its
 * navigation with {@link HIERARCHY_PICKER_CHANGE_STATE}. Features whose sub-path belongs to the
 * previous node (e.g. a location or equipment id) use it to restart at their root.
 * Re-selecting the selected node is no switch: its marker is dropped so the current page stays.
 */
export const useIsHierarchyPickerSwitch = (): boolean => {
  const hierarchyId = useSuiteHierarchyId()
  const {pathname, search, state} = useLocation()
  const navigate = useNavigate()
  const renderedHierarchyId = useRef<string | null>(null)

  const isPickerChange =
    (state as {hierarchyChangedBy?: string} | null)?.hierarchyChangedBy ===
    HIERARCHY_PICKER_CHANGE_STATE.hierarchyChangedBy
  const isSwitch = isPickerChange && renderedHierarchyId.current !== hierarchyId

  useEffect(() => {
    renderedHierarchyId.current = hierarchyId
    if (isPickerChange && !isSwitch) {
      void navigate(`${pathname}${search}`, {replace: true})
    }
  }, [hierarchyId, isPickerChange, isSwitch, navigate, pathname, search])

  return isSwitch
}
